import { useEffect } from "react";

/**
 * Makes typing on phones pleasant site-wide (mounted once, next to the
 * BottomNav in Navbar — so every layout that shows the bottom nav has it):
 *
 *  1. While a text field is focused (the on-screen keyboard is up) it adds
 *     `kb-open` to <html>. index.css hides every `[data-hide-on-keyboard]`
 *     element under that class — the bottom nav, the install-app banner and
 *     sticky action bars — so nothing fixed to the bottom of the screen can sit
 *     on top of the field being typed into.
 *  2. Once the keyboard has animated in (and whenever its height changes) it
 *     checks whether the focused field is inside the part of the screen that
 *     is still visible, and if not scrolls it into view (centred, clear of the
 *     sticky header via the scroll-margin in index.css). scrollIntoView also
 *     handles fields inside scrollable modals.
 *
 * Paired with `interactive-widget=resizes-content` in index.html, which makes
 * Android Chrome shrink the page above the keyboard instead of covering it.
 * Desktop / fine pointers are untouched.
 */

const TYPING_INPUT_TYPES = new Set([
  "",
  "text",
  "search",
  "email",
  "tel",
  "url",
  "number",
  "password",
]);

const isTypingTarget = (el) => {
  if (!el || el.disabled || el.readOnly) return false;
  if (el.isContentEditable) return true;
  if (el.tagName === "TEXTAREA") return true;
  if (el.tagName !== "INPUT") return false;
  return TYPING_INPUT_TYPES.has((el.getAttribute("type") || "").toLowerCase());
};

// Sticky top navbar height + a little breathing room.
const HEADER_CLEARANCE = 72;
const KEYBOARD_ANIMATION_MS = 320;

const MobileKeyboardManager = () => {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(max-width: 1020px) and (pointer: coarse)");
    const root = document.documentElement;
    const vv = window.visualViewport;
    let settleTimer;
    let blurTimer;

    const revealField = (el) => {
      if (!el || !document.contains(el) || document.activeElement !== el) return;
      const rect = el.getBoundingClientRect();
      const visibleTop = (vv ? vv.offsetTop : 0) + HEADER_CLEARANCE;
      const visibleBottom = (vv ? vv.offsetTop + vv.height : window.innerHeight) - 12;
      if (rect.top >= visibleTop && rect.bottom <= visibleBottom) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
    };

    const scheduleReveal = (el, delay) => {
      clearTimeout(settleTimer);
      settleTimer = setTimeout(() => revealField(el), delay);
    };

    const onFocusIn = (e) => {
      if (!mq.matches || !isTypingTarget(e.target)) return;
      clearTimeout(blurTimer);
      root.classList.add("kb-open");
      scheduleReveal(e.target, KEYBOARD_ANIMATION_MS);
    };

    const onFocusOut = () => {
      clearTimeout(blurTimer);
      // Focus moving field → field fires focusout then focusin; wait a beat so
      // the bottom nav doesn't flash back in between.
      blurTimer = setTimeout(() => {
        if (!isTypingTarget(document.activeElement)) {
          root.classList.remove("kb-open");
        }
      }, 120);
    };

    // The keyboard can close WITHOUT a focusout (Android's back button keeps
    // the input focused; a focused field removed from the page can skip it),
    // which would leave the bottom nav and sticky action bars hidden. So the
    // visible height decides too: tallest height seen = keyboard closed.
    const viewportHeight = () =>
      vv ? vv.height * (vv.scale || 1) : window.innerHeight;
    let fullHeight = viewportHeight();
    const onViewportResize = () => {
      const h = viewportHeight();
      if (h > fullHeight) fullHeight = h;
      const keyboardUp = h < fullHeight * 0.85;
      const el = document.activeElement;
      if (keyboardUp && isTypingTarget(el) && mq.matches) {
        root.classList.add("kb-open");
        scheduleReveal(el, 60);
      } else if (!keyboardUp) {
        root.classList.remove("kb-open");
      }
    };
    const onOrientationChange = () => {
      // New orientation, new "full" height — re-measure once it settles.
      setTimeout(() => {
        fullHeight = viewportHeight();
      }, 400);
    };

    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    vv?.addEventListener("resize", onViewportResize);
    window.addEventListener("orientationchange", onOrientationChange);
    return () => {
      clearTimeout(settleTimer);
      clearTimeout(blurTimer);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      vv?.removeEventListener("resize", onViewportResize);
      window.removeEventListener("orientationchange", onOrientationChange);
      root.classList.remove("kb-open");
    };
  }, []);

  return null;
};

export default MobileKeyboardManager;
